import { useCallback, useMemo, useState } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import type { OverallDescriptionDocType } from '@ast-data/shared';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import FieldCardList, { type FieldCardRow } from './FieldCardList';
import { useShownJson } from '../users/personLabels';
import type { FieldSortOption } from './fieldListSort';

const SORT = {
    listId: 'overallDescriptions',
    options: [
        { key: 'number', labelId: 'field.sort.number' },
        { key: 'updatedAt', labelId: 'field.sort.updatedAt', direction: 'desc' }
    ] satisfies FieldSortOption[]
};

/**
 * Touch-first list of a project's Gesamtbeschreibungen, with fast add. Selecting
 * one (or Add) opens the office Gesamtbeschreibung fullscreen dialog over this
 * list, like the Befunde category — see [[field-reuses-office-dialogs]].
 */
export default function FieldOverallDescriptionsPage() {
    const intl = useIntl();
    const navigate = useNavigate();
    const { projectId = '' } = useParams<{ projectId: string }>();

    const overallDescriptionsQuery = useStableQuery<OverallDescriptionDocType>({
        selector: { projectId }
    });
    const { results: overallDescriptionsDocs } =
        useLiveRxQuery<OverallDescriptionDocType>({
            collection: 'overall_descriptions',
            query: overallDescriptionsQuery
        });
    const overallDescriptions = useShownJson<OverallDescriptionDocType>(
        'overallDescription',
        overallDescriptionsDocs
    );

    const ability = useAbility();
    const canCreate = ability.can(
        'create',
        scopedSubject('OverallDescription', { projectId })
    );

    const [search, setSearch] = useState('');

    const rows = useMemo<FieldCardRow[]>(
        () =>
            overallDescriptions.map((overallDescription) => ({
                id: overallDescription.id,
                title: overallDescription.overallDescriptionNumber
                    ? intl.formatMessage(
                          { id: 'overallDescription.label' },
                          {
                              number: overallDescription.overallDescriptionNumber
                          }
                      )
                    : intl.formatMessage({
                          id: 'overallDescription.form.newTitle'
                      }),
                subtitle: overallDescription.designation || undefined,
                sortKey: overallDescription.overallDescriptionNumber,
                sortValues: {
                    number: overallDescription.overallDescriptionNumber,
                    updatedAt: overallDescription.updatedAt
                },
                searchText: [
                    overallDescription.overallDescriptionNumber,
                    overallDescription.designation
                ]
                    .filter(Boolean)
                    .join(' ')
            })),
        [overallDescriptions, intl]
    );

    const handleSelect = useCallback(
        (id: string) =>
            navigate(`/field/${projectId}/overall-descriptions/${id}/edit`),
        [navigate, projectId]
    );

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <FieldCardList
                rows={rows}
                onSelect={handleSelect}
                emptyMessageId="field.overallDescriptions.empty"
                noMatchesMessageId="field.overallDescriptions.noMatches"
                sort={SORT}
                search={search}
                onSearchChange={setSearch}
                toolbar={
                    canCreate ? (
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                navigate(
                                    `/field/${projectId}/overall-descriptions/new`
                                )
                            }
                        >
                            <FormattedMessage id="overallDescription.add" />
                        </Button>
                    ) : undefined
                }
            />
            {/* The Gesamtbeschreibung dialog routes render here, over the list. */}
            <Outlet />
        </Box>
    );
}
